import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'
import epsClient from 'enrollment-platform/api/eps.client'
import {
  BulkGrantAccessRequestDto,
  BulkGrantAccessResponseDto,
  CustomerOption,
  GetAllUsersRequestDto,
  GetAllUsersResponseDto,
  GetUsersRequestDto,
  GetUsersResponseDto,
  InviteRole,
  RemoveFundingAccessRequestDto,
  RemoveFundingAccessResponseDto,
  RevokeCompanyAccessRequestDto,
  RevokeCompanyAccessResponseDto,
  UserInvitationHistoryResponseDto,
} from './dto/user-invitation.dto'

const USERS_BASE = '/users'

/**
 * The RBAC roles assignable via the invite/assign flow (active invite-allowed
 * roles; Finance Manager only in v1). Served from the users controller so the
 * dropdown never calls the RBAC-Admin-only access-control roles endpoint.
 */
export const getInviteRoles = async (): Promise<InviteRole[]> => {
  const response = await apiClient.get<InviteRole[]>(
    `${USERS_BASE}/invite-roles`
  )
  return response.data
}

/**
 * Companies, statuses and addresses go over as one comma-separated value each:
 * none of them contains a comma, and it keeps the query string readable in
 * logs.
 */
const toUserQuery = ({
  clientPublicKeys,
  statuses,
  emails,
  ...rest
}: GetUsersRequestDto): Record<string, unknown> => ({
  ...rest,
  clientPublicKeys: clientPublicKeys?.length
    ? clientPublicKeys.join(',')
    : undefined,
  statuses: statuses?.length ? statuses.join(',') : undefined,
  emails: emails?.length ? emails.join(',') : undefined,
})

export const getUsers = async (
  params: GetUsersRequestDto = {}
): Promise<GetUsersResponseDto> => {
  const response = await apiClient.get<GetUsersResponseDto>(USERS_BASE, {
    params: toUserQuery(params),
  })
  return response.data
}

/**
 * Every user matching the filters, in one call. Serves both exporting the
 * roster and selecting all of it, neither of which can work from the page in
 * hand. Capped server-side, so compare `total` against the rows before
 * treating the answer as complete.
 */
export const getAllUsers = async (
  params: GetAllUsersRequestDto = {}
): Promise<GetAllUsersResponseDto> => {
  const response = await apiClient.get<GetAllUsersResponseDto>(
    `${USERS_BASE}/all`,
    { params: toUserQuery(params) }
  )
  return response.data
}

/**
 * Grants finance access to a batch of users. The caller does not choose per
 * recipient: the server classifies each one and invites, grants roles, or skips
 * it with a reason. Send large batches through `useBulkGrantAccessMutation`,
 * which splits them into short requests.
 */
export const bulkGrantAccess = async (
  body: BulkGrantAccessRequestDto
): Promise<BulkGrantAccessResponseDto> => {
  const response = await apiClient.post<BulkGrantAccessResponseDto>(
    `${USERS_BASE}/bulk-grant-access`,
    body
  )
  return response.data
}

/**
 * Takes funding access away from a batch of users entirely: withdraws a live
 * activation link, stops the invoices, empties the administered company list
 * and removes the roles. Send large batches through
 * `useRemoveFundingAccessMutation`, which splits them into short requests.
 */
export const removeFundingAccess = async (
  body: RemoveFundingAccessRequestDto
): Promise<RemoveFundingAccessResponseDto> => {
  const response = await apiClient.post<RemoveFundingAccessResponseDto>(
    `${USERS_BASE}/remove-funding-access`,
    body
  )
  return response.data
}

/**
 * Takes one company away from a batch of users instead of their funding access
 * as a whole, for when a billing profile stops listing their address. The roles
 * come off only when the company was the last one the user administered. Send
 * large batches through `useRevokeCompanyAccessMutation`.
 */
export const revokeCompanyAccess = async (
  body: RevokeCompanyAccessRequestDto
): Promise<RevokeCompanyAccessResponseDto> => {
  const response = await apiClient.post<RevokeCompanyAccessResponseDto>(
    `${USERS_BASE}/revoke-company-access`,
    body
  )
  return response.data
}

export const getUserHistory = async (
  userId: number
): Promise<UserInvitationHistoryResponseDto> => {
  const response = await apiClient.get<UserInvitationHistoryResponseDto>(
    `${USERS_BASE}/${userId}/history`
  )
  return response.data
}

interface CustomerListResultDto {
  data: { id: string; name: string }[]
  meta: { total: number; page: number; limit: number }
}

const CUSTOMER_PAGE_LIMIT = 500

const fetchCustomerPage = async (
  page: number
): Promise<CustomerListResultDto> => {
  const response = await epsClient.get<CustomerListResultDto>('/customers', {
    params: { page, limit: CUSTOMER_PAGE_LIMIT },
  })
  return response.data
}

/**
 * Every customer, not just the first page: these fill the company filter, and a
 * company missing from it cannot be filtered on at all.
 */
export const getCustomerOptions = async (): Promise<CustomerOption[]> => {
  const first = await fetchCustomerPage(1)
  const pageCount = Math.max(
    1,
    Math.ceil((first.meta?.total ?? 0) / CUSTOMER_PAGE_LIMIT)
  )
  const rest = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) =>
      fetchCustomerPage(index + 2)
    )
  )
  return [first, ...rest]
    .flatMap((result) => result?.data ?? [])
    .map((customer) => ({ id: customer.id, name: customer.name }))
}
